import styled, { css } from 'styled-components';
import Container from '~/grid/Container';
import Row from '~/grid/Row';
import createGrid from './helpers/createGrid';
import { ContainerLargeBgColor } from './ContainerLarge.helpers';
import getSpacing from '~/helpers/getSpacing';
import { mq } from '~/styles/grid';
import { TileWrapper } from '../../atoms/Tile';
import { getThemeValueByName } from '~/helpers/getColors';

/**
 * Styled wrapper div for the ContainerLarge component.
 * Applies background color, padding orientation, and theme-based styles.
 *
 * @typedef {Object} WrapperProps
 * @property {ContainerLargeBgColorType} $bgColor - Background color variant
 * @property {ContainerLargePaddingOrientation} $paddingOrientation - Padding orientation
 */
const Wrapper = styled.div`
  width: 100%;
  background: rgba(255, 255, 255, 1);

  @media (min-width: 1475px) {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 80px,
      rgba(255, 255, 255, 1) 81px,
      rgba(255, 255, 255, 1) 100%
    );
  }

  @media (${mq.xs}) {
    background: none;
  }

  ${(props) =>
    props.$paddingOrientation !== 'none' &&
    getSpacing(
      props.theme.spacingMap.containerLarge.padding,
      props.$paddingOrientation,
    )};

  ${(props) =>
    props.$bgColor === ContainerLargeBgColor.Brand &&
    css`
      background: ${getThemeValueByName(
        props.theme,
        props.theme.hero.background,
      )};
      color: ${props.theme.color.white500};

      &&& {
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        p,
        small,
        ul,
        ol,
        ol li:before,
        div.contactName,
        span {
          color: ${props.theme.color.white500};
        }

        a span,
        span svg {
          color: inherit;
        }

        div.accordionItemBar {
          background-color: ${props.theme.color.white500};
        }

        ul li:before {
          background: ${props.theme.color.white500};
        }

        div${TileWrapper} {
          background: ${props.theme.color.white500};

          span {
            color: ${props.theme.color.primary800};
          }
        }
        .select__control,
        .select__control .select__value-container {
          background-color: ${props.theme.color.white500};
        }
        .select__menu .select__option {
          background-color: ${props.theme.color.white500};
          border-top: 1px solid ${props.theme.color.gray500};
          &.select__option--is-focused {
            background-color: ${props.theme.color.gray200};
          }
        }
      }
    `}

  ${(props) =>
    props.$bgColor === ContainerLargeBgColor.Grey &&
    css`
      background: ${props.theme.color.gray50} !important;

      div${TileWrapper} {
        background: ${props.theme.color.white500};
      }

      &&& {
        .select__control,
        .select__control .select__value-container {
          background-color: ${props.theme.color.white500};
        }
        .select__menu .select__option {
          background-color: ${props.theme.color.white500};
          border-top: 1px solid ${props.theme.color.gray500};
          &.select__option--is-focused {
            background-color: ${props.theme.color.gray200};
          }
        }
      }
    `};
`;

/**
 * ContainerLarge component renders a large container with customizable background color,
 * padding orientation, and grid layout for its child elements.
 *
 * @param {ContainerLargeProps} props - Component props
 * @returns {React.ReactElement} The rendered ContainerLarge component
 */
const ContainerLarge = ({
  bgColor,
  paddingOrientation = 'py',
  type = 'A',
  customGutter,
  elements,
  className,
  isJobSearch = false,
}) => {
  return (
    <Wrapper
      className={className}
      $bgColor={bgColor}
      $paddingOrientation={paddingOrientation}
      data-id="container-large-wrapper"
    >
      <Container isJobSearch={isJobSearch} data-id="container-large-container">
        <Row gutter={customGutter}>
          {createGrid(type, elements, customGutter)}
        </Row>
      </Container>
    </Wrapper>
  );
};

export default ContainerLarge;
